/* A product in the PDP — designs/products-pdp.md. Everything that hangs
   under a product (src/shared/products.ts, nothing more) once it declares a
   phase: the declaration itself, the owed set DERIVED from the catalog and
   that one declaration (PP2), and later the evidence, gate reviews and
   certificates (PR 3/4). Pure rules only — the routes call these, the client
   says the same reasons sooner. The catalog is pdp.ts (pdp-catalog.md); the
   feed contract is feeds.ts (kpi-feed-seam.md). */

import type { Stamp, UserRef } from "./organizer-roles";
import { feedRefReason, type FeedReading, type FeedRef, type FeedSourceInfo } from "./feeds";
import { PDP_CRITERION_AREAS, PDP_PHASES, PDP_VERSION, standing, type PdpCriterion, type PdpDeliverable, type PdpGate, type PdpPhase, type PdpTarget } from "./pdp";
import type { Product } from "./products";

/* ---- the declaration (PP1/PP3) ---- */

/* "not-in-pdp" is a CONFIRMED value (Trevor, Sep 22), never a gap; a
   withdrawal returns the product to not recorded and stays in the history */
export type PhaseDeclaration =
  | { kind: "phase"; phase: PdpPhase; effective: string; unconfirmed: boolean; recorded: Stamp }
  | { kind: "not-in-pdp"; reason: string; recorded: Stamp }
  | { kind: "withdrawn"; recorded: Stamp };

export type PhaseDeclarationInput =
  | { kind: "phase"; phase: PdpPhase; effective: string; unconfirmed: boolean }
  | { kind: "not-in-pdp"; reason: string }
  | { kind: "withdrawn" };

/* ---- slots, evidence, reviews, certificates — the served shape (PP1) ----
   Evidence / GateReview / CertificateHead are the cross-design contract
   types, declared here so `ProductPdp` is whole from PR 2 on; PR 3 records
   evidence and PR 4 records reviews and issues certificates. */

export type SlotKey = `${string}@${PdpPhase}`;

export interface OwedSlot {
  deliverableId: string;
  phase: PdpPhase;
}

export const slotKey = (s: OwedSlot): SlotKey => `${s.deliverableId}@${s.phase}`;

export type Evidence =
  | { kind: "link"; url: string; title: string; unconfirmed: boolean; why?: string; recorded: Stamp }
  | { kind: "feed"; ref: FeedRef; criterionId: string | null; unconfirmed: boolean; why?: string; recorded: Stamp }
  | { kind: "estimate"; percent: number; note: string; recorded: Stamp }
  | { kind: "cleared"; recorded: Stamp };

export type GateDecision = "passed" | "passed-with-conditions" | "held";

export interface OpenGap {
  deliverableId: string;
  phase: PdpPhase;
  state: "missing" | "unconfirmed";
}

export interface GateReview {
  id: string;
  productId: string;
  gateId: string;
  decision: GateDecision;
  conditions: string | null;
  reason: string | null;
  reviewDate: string;
  minutesUrl: string | null;
  openGaps: OpenGap[];
  supersedes: string | null;
  supersededBy: string | null;
  recorded: Stamp;
}

export interface CertificateHead {
  id: string;
  phase: PdpPhase;
  gateReviewId: string;
  issuedAt: string;
  supersededBy: string | null;
}

/* a FeedReading rides a frozen certificate row (PP9) — named here so the
   shape PR 4 freezes is already the contract's */
export type FrozenPointer =
  | { kind: "link"; url: string; title: string }
  | { kind: "feed"; ref: FeedRef; reading: FeedReading }
  | { kind: "estimate"; percent: number; note: string }
  | null;

/* the frozen body (PP9): every owed slot of phases 0..N as it stood — `phase`
   is the one field added to the contract's rows, because rows span phases */
export interface FrozenRow {
  deliverableId: string;
  phase: PdpPhase;
  name: string;
  ownerRoleIds: string[];
  reviewerRoleIds: string[];
  state: SlotState;
  pointer: FrozenPointer;
}

/* a phase exit criterion as it stood, with the feed slot that names it: its
   state, the reading taken at issue, the catalog target and the measure */
export interface FrozenCriterion {
  id: string;
  area: string;
  text: string | null;
  evidenceState: SlotState;
  feedReading: FeedReading | null;
  target: PdpTarget | null;
  measure: string | null;
}

export interface FrozenCertificate {
  productName: string;
  version: string;
  decision: GateDecision;
  conditions: string | null;
  reviewDate: string;
  minutesUrl: string | null;
  approverRoleId: string | null;
  recordedBy: UserRef;
  percent: number | null;
  counts: SlotCounts;
  rows: FrozenRow[];
  criteria: FrozenCriterion[];
}

export interface PhaseCertificate extends CertificateHead {
  productId: string;
  frozen: FrozenCertificate;
}

/* what `declaration` can hold: a withdrawal is never current — it reads as
   null (PP1) — so the served shape says so and no view needs a dead branch */
export type CurrentDeclaration = Exclude<PhaseDeclaration, { kind: "withdrawn" }>;

export interface ProductPdp {
  productId: string;
  /* the current declaration; null = not recorded (red) */
  declaration: CurrentDeclaration | null;
  /* the whole append-only history, oldest first */
  declarations: PhaseDeclaration[];
  evidence: Record<SlotKey, Evidence[]>;
  reviews: GateReview[];
  certificates: CertificateHead[];
}

/* PP8: the precondition a gate review is written under. Every table behind
   the record is append-only, so its row counts ARE its version: equal counts
   mean nothing landed between the sheet's read and the write. The route
   takes it from the record it validated against; the store re-reads it
   inside the write transaction and refuses on a mismatch (Copilot review of
   #132 — certificate feed reads can hold the gap open for 10 s). */
export interface RecordVersion {
  declarations: number;
  evidence: number;
  reviews: number;
}

export const recordVersion = (pdp: ProductPdp): RecordVersion => ({
  declarations: pdp.declarations.length,
  evidence: Object.values(pdp.evidence).reduce((n, history) => n + history.length, 0),
  reviews: pdp.reviews.length,
});

export const sameVersion = (a: RecordVersion, b: RecordVersion): boolean =>
  a.declarations === b.declarations && a.evidence === b.evidence && a.reviews === b.reviews;

export const RECORD_CHANGED = "the record changed while the review was being prepared — reload and record again";

/* thrown by the store when the version no longer matches; the route answers 409 */
export class RecordChangedError extends Error {
  constructor() {
    super(RECORD_CHANGED);
    this.name = "RecordChangedError";
  }
}

/* the record of a product nothing has been recorded against yet */
export const emptyProductPdp = (productId: string): ProductPdp => ({
  productId,
  declaration: null,
  declarations: [],
  evidence: {},
  reviews: [],
  certificates: [],
});

/* The current declaration is the LATEST entry — unless that entry is a
   withdrawal, which returns the product to not recorded (UC step 6). The
   history is never edited, so this is the `roleRevs.at(-1)` idiom. */
export const currentDeclaration = (declarations: PhaseDeclaration[]): CurrentDeclaration | null => {
  const last = declarations.at(-1);
  return last && last.kind !== "withdrawn" ? last : null;
};

export const declaredPhase = (decl: CurrentDeclaration | null): PdpPhase | null =>
  decl?.kind === "phase" ? decl.phase : null;

/* the served record from the stored histories — the declaration list, one
   evidence history per slot ever recorded against, the reviews and the
   certificate heads with `supersededBy` DERIVED here, never read from a
   column (PP1/PP2/PP10) */
export const productPdpFrom = (
  productId: string,
  declarations: PhaseDeclaration[],
  evidence: Record<SlotKey, Evidence[]> = {},
  reviews: GateReview[] = [],
  certificates: CertificateHead[] = [],
): ProductPdp => ({
  productId,
  declaration: currentDeclaration(declarations),
  declarations,
  evidence,
  reviews: reviews.map((r) => ({ ...r, supersededBy: reviewSupersededBy(r, reviews) })),
  certificates: certificates.map((c) => ({ ...c, supersededBy: certificateSupersededBy(c, reviews, certificates) })),
});

/* PP10: a review is superseded by the review naming it in `supersedes` */
export const reviewSupersededBy = (review: { id: string }, reviews: GateReview[]): string | null =>
  reviews.find((r) => r.supersedes === review.id)?.id ?? null;

/* PP10: a certificate is superseded by the certificate of the review that
   superseded its own review — or by `held:{reviewId}` when that superseding
   decision was held and so issued none (the phase went back, PP10) */
export function certificateSupersededBy(cert: CertificateHead, reviews: GateReview[], certificates: CertificateHead[]): string | null {
  const by = reviews.find((r) => r.supersedes === cert.gateReviewId);
  if (!by) return null;
  if (by.decision === "held") return `held:${by.id}`;
  return certificates.find((c) => c.gateReviewId === by.id)?.id ?? null;
}

export const certificateHead = (cert: PhaseCertificate): CertificateHead => ({
  id: cert.id,
  phase: cert.phase,
  gateReviewId: cert.gateReviewId,
  issuedAt: cert.issuedAt,
  supersededBy: cert.supersededBy,
});

/* the route's `:slotKey` back into a slot — `{deliverableId}@{phase}`, the
   phase a single digit 0–6; anything else is not a slot at all */
export function parseSlotKey(key: string): OwedSlot | null {
  const m = /^([^@]+)@([0-6])$/.exec(key);
  return m ? { deliverableId: m[1]!, phase: Number(m[2]) as PdpPhase } : null;
}

/* ---- the owed set (PP2) ---- */

export interface OwedRow extends OwedSlot {
  row: PdpDeliverable;
  /* an iterate row's slot in a phase after its own — "(Phase n repeat)" */
  repeat: boolean;
}

const lt = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

/* Every standing row with phase ≤ declared owes one slot at its own phase;
   `iterate: true` owes one slot per phase from its phase to the declared
   one. Ordered swimlane · path · name · phase. No phase → nothing owed:
   not in the PDP, withdrawn and not recorded all owe []. Never stored. */
export function owedRows(catalog: PdpDeliverable[], declared: PdpPhase | null): OwedRow[] {
  if (declared === null) return [];
  const rows = standing(catalog)
    .filter((r) => r.phase <= declared)
    .sort(
      (a, b) =>
        lt(a.swimlane, b.swimlane) ||
        lt(a.path, b.path) ||
        lt(a.name.toLowerCase(), b.name.toLowerCase()) ||
        a.phase - b.phase,
    );
  const out: OwedRow[] = [];
  for (const row of rows) {
    const last = row.iterate ? declared : row.phase;
    for (const phase of PDP_PHASES)
      if (phase >= row.phase && phase <= last)
        out.push({ deliverableId: row.id, phase, row, repeat: phase !== row.phase });
  }
  return out;
}

export const owedSlots = (catalog: PdpDeliverable[], declared: PdpPhase | null): OwedSlot[] =>
  owedRows(catalog, declared).map(({ deliverableId, phase }) => ({ deliverableId, phase }));

/* ---- rows with their state, filters and counts (PP16) ---- */

/* an owed row joined to what was recorded against its slot */
export interface SlotRow extends OwedRow {
  state: SlotState;
  history: Evidence[];
  current: CurrentEvidence | null;
}

export const slotRows = (rows: OwedRow[], evidence: Record<SlotKey, Evidence[]>): SlotRow[] =>
  rows.map((r) => {
    const history = evidence[slotKey(r)] ?? [];
    return { ...r, state: slotState(history), history, current: currentEvidence(history) };
  });

export interface OwedFilter {
  phase: PdpPhase | "all";
  state: SlotState | "all";
  search: string;
}

export const ALL_OWED: OwedFilter = { phase: "all", state: "all", search: "" };

/* swimlane → path → rows, in owed order; a group with no surviving row is
   dropped. Search matches name, path and the owner / reviewer role names,
   which the caller resolves (the shared module holds no roles list). */
export interface OwedGroup {
  swimlane: string;
  paths: { path: string; rows: SlotRow[] }[];
}

export function groupOwedRows(rows: SlotRow[], filter: OwedFilter = ALL_OWED, roleName: (id: string) => string = (id) => id): OwedGroup[] {
  const q = filter.search.trim().toLowerCase();
  const hit = (r: SlotRow): boolean =>
    (filter.phase === "all" || r.phase === filter.phase) &&
    (filter.state === "all" || r.state === filter.state) &&
    (q === "" ||
      [r.row.name, r.row.path, ...r.row.ownerRoleIds.map(roleName), ...r.row.reviewerRoleIds.map(roleName)].some((t) =>
        t.toLowerCase().includes(q),
      ));
  const groups: OwedGroup[] = [];
  for (const r of rows) {
    if (!hit(r)) continue;
    let lane = groups.at(-1);
    if (!lane || lane.swimlane !== r.row.swimlane) {
      lane = { swimlane: r.row.swimlane, paths: [] };
      groups.push(lane);
    }
    let way = lane.paths.at(-1);
    if (!way || way.path !== r.row.path) {
      way = { path: r.row.path, rows: [] };
      lane.paths.push(way);
    }
    way.rows.push(r);
  }
  return groups;
}

/* the counts line over the rows given — the caller passes the phase
   filter's set, so the line agrees with that phase's bar */
export interface OwedCounts extends SlotCounts {
  owed: number;
}

export function owedCounts(rows: SlotRow[]): OwedCounts {
  const counts: OwedCounts = { owed: rows.length, evidenced: 0, estimated: 0, missing: 0, unconfirmed: 0 };
  for (const r of rows) counts[COUNT_KEY[r.state]] += 1;
  return counts;
}

/* ---- declaration rules (PP3) ---- */

const REASON_MAX = 300;
const KIND_REASON = "the declaration is a phase, not in the PDP, or a withdrawal";
const DAY = /^\d{4}-\d{2}-\d{2}$/;

/* a real calendar day: the shape AND a date that round-trips (2026-02-31
   and 2026-13-01 match the regex but are not days Postgres will take; nor
   is year 0000, which JS round-trips and Postgres DATE has no year for) */
const isCalendarDay = (value: unknown): value is string =>
  typeof value === "string" &&
  DAY.test(value) &&
  value >= "0001-01-01" &&
  !Number.isNaN(Date.parse(`${value}T00:00:00Z`)) &&
  new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;

const isPhase = (value: unknown): value is PdpPhase =>
  typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= 6;

/* `today` is the UTC calendar day, YYYY-MM-DD — the server's and the form's
   alike (`utcToday`); a plain string compare orders calendar days */
export const utcToday = (): string => new Date().toISOString().slice(0, 10);

export function declarationReason(
  input: unknown,
  today: string,
  current: CurrentDeclaration | null,
  productName: string,
): string | null {
  if (typeof input !== "object" || input === null) return KIND_REASON;
  const body = input as Record<string, unknown>;
  switch (body.kind) {
    case "phase": {
      if (!isPhase(body.phase)) return "the phase is 0 to 6";
      if (!isCalendarDay(body.effective)) return "the effective date is a calendar day, YYYY-MM-DD";
      if (body.effective > today) return "an effective date in the future is a plan, not a declaration";
      if (typeof body.unconfirmed !== "boolean") return "unconfirmed is true or false";
      return null;
    }
    case "not-in-pdp": {
      if (typeof body.reason !== "string" || !body.reason.trim())
        return `say in one line why ${productName} is outside the PDP`;
      if (body.reason.trim().length > REASON_MAX) return `the reason is at most ${REASON_MAX} characters`;
      return null;
    }
    case "withdrawn":
      return current === null ? "nothing to withdraw" : null;
    default:
      return KIND_REASON;
  }
}

/* the stamped declaration a validated input becomes — the route stamps, the
   store takes stamped values (PP11) */
export function stampDeclaration(input: PhaseDeclarationInput, by: UserRef, source: string, date: string): PhaseDeclaration {
  const recorded: Stamp = { source, date, by };
  switch (input.kind) {
    case "phase":
      return { kind: "phase", phase: input.phase, effective: input.effective, unconfirmed: input.unconfirmed, recorded };
    case "not-in-pdp":
      return { kind: "not-in-pdp", reason: input.reason.trim(), recorded };
    case "withdrawn":
      return { kind: "withdrawn", recorded };
  }
}

/* ---- evidence rules (PP4) ---- */

/* what the evidence route takes — `Evidence` minus `recorded`; `cleared`
   is a revision like any other (UC step 7: the cleared pointer stays in
   the history) */
export type EvidenceInput =
  | { kind: "link"; url: string; title: string; unconfirmed: boolean; why?: string }
  | { kind: "feed"; ref: FeedRef; criterionId: string | null; unconfirmed: boolean; why?: string }
  | { kind: "estimate"; percent: number; note: string }
  | { kind: "cleared" };

/* Trevor's cap (decision 4): 0–90; that close to done the artifact exists */
export const ESTIMATE_MAX = 90;
export const ESTIMATE_CAP_REFUSAL = "that close to done, link the artifact — estimates stop at 90";
export const UNCONFIRMED_WHY_REFUSAL = "an unconfirmed pointer needs the reason";
const EVIDENCE_KIND_REASON = "the evidence is a link, a feed, an estimate, or a clear";
const URL_MAX = 2000;
const TITLE_MAX = 200;

const isHttpUrl = (value: unknown): value is string => {
  if (typeof value !== "string") return false;
  try {
    const u = new URL(value.trim());
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

/* the pointer kinds share the confidence rule (CH12's dangling-unconfirmed
   guard applied to pointers): the flag is a boolean, true needs a why */
const confidenceReason = (body: Record<string, unknown>): string | null => {
  if (typeof body.unconfirmed !== "boolean") return "unconfirmed is true or false";
  if (!body.unconfirmed) return null;
  if (typeof body.why !== "string" || !body.why.trim()) return UNCONFIRMED_WHY_REFUSAL;
  if (body.why.trim().length > REASON_MAX) return `the reason is at most ${REASON_MAX} characters`;
  return null;
};

/* `history` is the slot's evidence so far (a clear needs something to
   clear); `sources` is the server's feed list when the caller has it. null =
   acceptable. Hermes never checks that a link resolves. */
export function evidenceReason(input: unknown, history: Evidence[], sources?: readonly FeedSourceInfo[]): string | null {
  if (typeof input !== "object" || input === null) return EVIDENCE_KIND_REASON;
  const body = input as Record<string, unknown>;
  switch (body.kind) {
    case "link": {
      if (!isHttpUrl(body.url)) return "a link needs an absolute http(s) URL";
      if (body.url.trim().length > URL_MAX) return `the URL is at most ${URL_MAX} characters`;
      if (typeof body.title !== "string" || !body.title.trim()) return "give the link a title, as the row will show it";
      if (body.title.trim().length > TITLE_MAX) return `the title is at most ${TITLE_MAX} characters`;
      return confidenceReason(body);
    }
    case "feed": {
      const bad = feedRefReason(body.ref, sources);
      if (bad) return bad;
      if (body.criterionId !== null && typeof body.criterionId !== "string") return "the criterion is a catalog id or null";
      return confidenceReason(body);
    }
    case "estimate": {
      const p = body.percent;
      if (typeof p !== "number" || !Number.isInteger(p) || p < 0 || p > 100) return `the estimate is 0 to ${ESTIMATE_MAX}`;
      if (p > ESTIMATE_MAX) return ESTIMATE_CAP_REFUSAL;
      if (typeof body.note !== "string" || !body.note.trim()) return "say in one line what the estimate rests on";
      if (body.note.trim().length > REASON_MAX) return `the note is at most ${REASON_MAX} characters`;
      if ("unconfirmed" in body) return "an estimate carries no unconfirmed flag";
      return null;
    }
    case "cleared":
      return slotState(history) === "missing" ? "nothing to clear" : null;
    default:
      return EVIDENCE_KIND_REASON;
  }
}

/* the stamped revision a validated input becomes — the route stamps, the
   store takes stamped values (PP11) */
export function stampEvidence(input: EvidenceInput, by: UserRef, source: string, date: string): Evidence {
  const recorded: Stamp = { source, date, by };
  const why = "unconfirmed" in input && input.unconfirmed && input.why !== undefined ? { why: input.why.trim() } : {};
  switch (input.kind) {
    case "link":
      return { kind: "link", url: input.url.trim(), title: input.title.trim(), unconfirmed: input.unconfirmed, ...why, recorded };
    case "feed":
      return {
        kind: "feed",
        ref: { sourceId: input.ref.sourceId, measure: input.ref.measure.trim() },
        criterionId: input.criterionId,
        unconfirmed: input.unconfirmed,
        ...why,
        recorded,
      };
    case "estimate":
      return { kind: "estimate", percent: input.percent, note: input.note.trim(), recorded };
    case "cleared":
      return { kind: "cleared", recorded };
  }
}

/* ---- slot state (PP5) ---- */

export type SlotState = "missing" | "estimated" | "unconfirmed" | "evidence";

/* the latest revision decides: none or cleared → missing; an estimate →
   estimated; a pointer flagged unconfirmed → unconfirmed; else evidence.
   A feed's reachability never enters (KF14) — the pointer is the evidence. */
export function slotState(history: Evidence[]): SlotState {
  const last = history.at(-1);
  if (!last || last.kind === "cleared") return "missing";
  if (last.kind === "estimate") return "estimated";
  return last.unconfirmed ? "unconfirmed" : "evidence";
}

/* the slot's standing value: the latest revision unless that is a clear —
   what the drawer prefills and the row renders */
export type CurrentEvidence = Exclude<Evidence, { kind: "cleared" }>;

export const currentEvidence = (history: Evidence[]): CurrentEvidence | null => {
  const last = history.at(-1);
  return last && last.kind !== "cleared" ? last : null;
};

/* ---- the derived percent (PP6) ---- */

export interface SlotCounts {
  evidenced: number;
  estimated: number;
  missing: number;
  unconfirmed: number;
}

export interface PhaseBar {
  phase: PdpPhase;
  /* null = the phase owes nothing */
  percent: number | null;
  counts: SlotCounts;
}

/* what one slot contributes: a pointer 100 (a feed whatever its reading,
   KF14), an estimate its value, missing and unconfirmed 0 — every owed slot
   weighs 1, nothing on the product accepts a percent */
const contribution = (history: Evidence[]): number => {
  const state = slotState(history);
  if (state === "evidence") return 100;
  if (state === "estimated") return (history.at(-1) as { percent: number }).percent;
  return 0;
};

const COUNT_KEY: Record<SlotState, keyof SlotCounts> = {
  evidence: "evidenced",
  estimated: "estimated",
  missing: "missing",
  unconfirmed: "unconfirmed",
};

/* over the slots owed IN `phase`: the rounded mean, null when none */
export function phasePercent(
  slots: OwedSlot[],
  evidence: Record<SlotKey, Evidence[]>,
  phase: PdpPhase,
): { percent: number | null; counts: SlotCounts } {
  const counts: SlotCounts = { evidenced: 0, estimated: 0, missing: 0, unconfirmed: 0 };
  let sum = 0;
  let n = 0;
  for (const slot of slots) {
    if (slot.phase !== phase) continue;
    const history = evidence[slotKey(slot)] ?? [];
    counts[COUNT_KEY[slotState(history)]] += 1;
    sum += contribution(history);
    n += 1;
  }
  return { percent: n === 0 ? null : Math.round(sum / n), counts };
}

/* one bar per phase 0..declared and nothing beyond — an unreached phase is
   not "0 % complete", it is not owed yet */
export function phaseBars(catalog: PdpDeliverable[], pdp: ProductPdp): PhaseBar[] {
  const declared = declaredPhase(pdp.declaration);
  if (declared === null) return [];
  const slots = owedSlots(catalog, declared);
  return PDP_PHASES.filter((p) => p <= declared).map((phase) => ({ phase, ...phasePercent(slots, pdp.evidence, phase) }));
}

/* ---- gate review rules (PP7) ---- */

/* what the review route takes — `GateReview` minus id, product, gaps and
   stamp: the gaps are computed server-side at save, never taken from the
   body (UC constraint: stamped, not blocking) */
export interface GateReviewInput {
  gateId: string;
  decision: GateDecision;
  conditions: string | null;
  reason: string | null;
  reviewDate: string;
  minutesUrl: string | null;
  supersedes: string | null;
}

export const GATE_DECISIONS: readonly GateDecision[] = ["passed", "passed-with-conditions", "held"];
const DECISION_REASON = "the decision is passed, passed with conditions, or held";
const REVIEW_TEXT_MAX = 2000;
export const NOT_IN_PDP_REVIEW = (name: string): string => `${name} is not in the PDP`;
export const DECLARE_FIRST = "declare a phase before recording a gate review";
export const SUPERSEDES_REASON = "supersedes must name an earlier review of this gate";
export const ALREADY_SUPERSEDED = "that review is already superseded — supersede the one in force";

const isDecision = (value: unknown): value is GateDecision => GATE_DECISIONS.includes(value as GateDecision);
const textOrNull = (value: unknown): value is string | null => value === null || typeof value === "string";
const blank = (value: string | null): boolean => value === null || value.trim() === "";

/* The shape rules alone — what the sheet can say before the round trip
   (the client subset of PP7); the record rules below need the product's
   record and the catalog and stay on the server. null = acceptable. */
export function gateReviewShapeReason(input: unknown, today: string): string | null {
  if (typeof input !== "object" || input === null) return DECISION_REASON;
  const body = input as Record<string, unknown>;
  if (!isDecision(body.decision)) return DECISION_REASON;
  if (!textOrNull(body.conditions) || !textOrNull(body.reason)) return "conditions and reasons are text or null";
  if (body.decision === "passed-with-conditions" && blank(body.conditions)) return "say what the conditions are";
  if (body.decision === "held" && blank(body.reason)) return "say why the gate is held";
  /* a pass that replaces an earlier review owes the record the why as much
     as a hold does (PP10 audit); a held supersede's reason for hold IS it */
  if (body.decision !== "held" && body.supersedes !== null && body.supersedes !== undefined && blank(body.reason as string | null))
    return "say why this review supersedes the earlier one";
  if ((body.conditions ?? "").length > REVIEW_TEXT_MAX || (body.reason ?? "").length > REVIEW_TEXT_MAX)
    return `conditions and reasons are at most ${REVIEW_TEXT_MAX} characters`;
  if (body.reviewDate === undefined || body.reviewDate === "") return "the review date is required";
  if (!isCalendarDay(body.reviewDate)) return "the review date is a calendar day, YYYY-MM-DD";
  if (body.reviewDate > today) return "the review date is not after today";
  if (body.minutesUrl !== null && !isHttpUrl(body.minutesUrl)) return "the minutes link needs an absolute http(s) URL";
  return null;
}

/* PP7 whole: the shape, then the record — a retired product is read-only,
   a phase must stand, the gate must be the declared phase's exit unless the
   review supersedes an earlier review of that gate (PP10: a held review may
   pull a pass back after the phase moved on), and `supersedes` names a
   review of this product and gate that is still in force. */
export function gateReviewReason(input: unknown, gates: PdpGate[], pdp: ProductPdp, product: Product, today: string): string | null {
  const shape = gateReviewShapeReason(input, today);
  if (shape) return shape;
  const body = input as Record<string, unknown>;
  if (product.retired) return `${product.name} is retired — its PDP record is read-only`;
  const decl = pdp.declaration;
  if (decl === null) return DECLARE_FIRST;
  if (decl.kind !== "phase") return NOT_IN_PDP_REVIEW(product.name);
  const gate = gates.find((g) => g.id === body.gateId);
  if (!gate) return "that gate is not in the catalog";
  let superseded: GateReview | undefined;
  if (body.supersedes !== null) {
    superseded = pdp.reviews.find((r) => r.id === body.supersedes && r.gateId === gate.id);
    if (!superseded) return SUPERSEDES_REASON;
    if (pdp.reviews.some((r) => r.supersedes === superseded!.id)) return ALREADY_SUPERSEDED;
  }
  if (gate.afterPhase !== decl.phase && !superseded)
    return `${gate.label} follows Phase ${gate.afterPhase}; ${product.name} is in Phase ${decl.phase}`;
  return null;
}

/* the owed rows not recorded or unconfirmed at this instant, in owed order —
   computed server-side at save from the live slots, never taken from the
   body (PP7); an undeclared or not-in-PDP product owes nothing, so has none */
export function openGapsOf(catalog: PdpDeliverable[], pdp: ProductPdp): OpenGap[] {
  return slotRows(owedRows(catalog, declaredPhase(pdp.declaration)), pdp.evidence)
    .filter((r) => r.state === "missing" || r.state === "unconfirmed")
    .map((r) => ({ deliverableId: r.deliverableId, phase: r.phase, state: r.state as OpenGap["state"] }));
}

const trimmedOrNull = (text: string | null): string | null => (text === null || text.trim() === "" ? null : text.trim());

/* the stored review a validated input becomes — the route mints the id,
   computes the gaps and stamps; `supersededBy` is derived on read (PP10) */
export function stampGateReview(
  id: string,
  productId: string,
  input: GateReviewInput,
  openGaps: OpenGap[],
  by: UserRef,
  source: string,
  date: string,
): GateReview {
  return {
    id,
    productId,
    gateId: input.gateId,
    decision: input.decision,
    conditions: trimmedOrNull(input.conditions),
    reason: trimmedOrNull(input.reason),
    reviewDate: input.reviewDate,
    minutesUrl: input.minutesUrl,
    openGaps,
    supersedes: input.supersedes,
    supersededBy: null,
    recorded: { source, date, by },
  };
}

/* ---- the frozen certificate (PP9) ---- */

/* the recorder a review carries — an imported review would have none, and
   the certificate says so rather than inventing one */
const NOBODY: UserRef = { userId: "", name: "not recorded" };

const frozenPointer = (current: CurrentEvidence | null, reading: FeedReading | undefined): FrozenPointer => {
  if (!current) return null;
  switch (current.kind) {
    case "link":
      return { kind: "link", url: current.url, title: current.title };
    case "feed":
      return { kind: "feed", ref: current.ref, reading: reading ?? { state: "not-read-yet" } };
    case "estimate":
      return { kind: "estimate", percent: current.percent, note: current.note };
  }
};

export interface FreezeInput {
  product: Product;
  deliverables: PdpDeliverable[];
  criteria: PdpCriterion[];
  gate: PdpGate;
  pdp: ProductPdp;
  review: GateReview;
  /* the readings taken at issue, per feed-evidenced slot (KF15); a slot
     with none freezes not-read-yet */
  readings: Partial<Record<SlotKey, FeedReading>>;
  issuedAt: string;
}

/* The feed slot naming a criterion, if one stands — the phase's own slot
   first (an iterate row may name it from an earlier phase too). One rule for
   the certificate (PP9), the Phase area's criteria and the review sheet. */
export const feedSlotFor = (rows: SlotRow[], criterionId: string, phase: PdpPhase): SlotRow | undefined => {
  const names = (r: SlotRow): boolean => r.current?.kind === "feed" && r.current.criterionId === criterionId;
  return rows.find((r) => r.phase === phase && names(r)) ?? rows.find(names);
};

/* Pure: every owed slot of phases 0..N (N = the gate's phase) with its state
   and pointer as it stood, PP6's percent and counts for phase N, and the
   phase's criteria each with the feed slot that names it. The server's
   issueCertificate takes the readings first, then calls this. */
export function freezeCertificate({ product, deliverables, criteria, gate, pdp, review, readings, issuedAt }: FreezeInput): PhaseCertificate {
  const phase = gate.afterPhase;
  const rows = slotRows(owedRows(deliverables, phase), pdp.evidence);
  const frozenRows: FrozenRow[] = rows.map((r) => ({
    deliverableId: r.deliverableId,
    phase: r.phase,
    name: r.row.name,
    ownerRoleIds: r.row.ownerRoleIds,
    reviewerRoleIds: r.row.reviewerRoleIds,
    state: r.state,
    pointer: frozenPointer(r.current, readings[slotKey(r)]),
  }));
  const frozenCriteria: FrozenCriterion[] = criteria
    .filter((c) => c.phase === phase)
    .sort((a, b) => PDP_CRITERION_AREAS.indexOf(a.area) - PDP_CRITERION_AREAS.indexOf(b.area))
    .map((c) => {
      const named = feedSlotFor(rows, c.id, phase);
      return {
        id: c.id,
        area: c.area,
        text: c.text,
        evidenceState: named ? named.state : "missing",
        feedReading: named ? (readings[slotKey(named)] ?? { state: "not-read-yet" }) : null,
        target: c.target,
        measure: named?.current?.kind === "feed" ? named.current.ref.measure : null,
      };
    });
  const { percent, counts } = phasePercent(rows, pdp.evidence, phase);
  return {
    id: `${product.id}-p${phase}-${review.id}`,
    productId: product.id,
    phase,
    gateReviewId: review.id,
    issuedAt,
    supersededBy: null,
    frozen: {
      productName: product.name,
      version: PDP_VERSION,
      decision: review.decision,
      conditions: review.conditions,
      reviewDate: review.reviewDate,
      minutesUrl: review.minutesUrl,
      approverRoleId: gate.approverRoleId,
      recordedBy: review.recorded.by ?? NOBODY,
      percent,
      counts,
      rows: frozenRows,
      criteria: frozenCriteria,
    },
  };
}
